
/**
 * Utility functions for formatting data
 */

/**
 * Format bytes into a human-readable string
 * @param bytes The number of bytes
 * @param decimals The number of decimal places to include
 * @returns A formatted string (e.g., "1.5 MB")
 */
export function bytesToSize(bytes: number, decimals: number = 1): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Format a number with thousands separators
 * @param value The number to format
 * @returns A formatted string (e.g., "1,234,567")
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}

/**
 * Format a currency value
 * @param value The number to format
 * @param currency The currency code (default: 'USD')
 * @returns A formatted currency string (e.g., "$1,234.56")
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a percentage value
 * @param value The decimal value (0-1)
 * @param decimals The number of decimal places to include
 * @returns A formatted percentage string (e.g., "45.7%")
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return (value * 100).toFixed(decimals) + '%';
}

/**
 * Format a date into a readable string
 * @param date The date to format
 * @param format The format style ('short', 'medium', 'long', 'full')
 * @returns A formatted date string
 */
export function formatDate(date: Date, format: 'short' | 'medium' | 'long' | 'full' = 'medium'): string {
  return new Intl.DateTimeFormat('default', {
    dateStyle: format
  }).format(date);
}

/**
 * Format a timestamp into a readable string with date and time
 * @param date The date to format
 * @param format The format style ('short', 'medium', 'long', 'full')
 * @returns A formatted date-time string
 */
export function formatDateTime(date: Date, format: 'short' | 'medium' | 'long' | 'full' = 'medium'): string {
  return new Intl.DateTimeFormat('default', {
    dateStyle: format,
    timeStyle: format
  }).format(date);
}

/**
 * Format a time duration in milliseconds into a readable string
 * @param ms The duration in milliseconds
 * @returns A formatted string (e.g., "2m 30s" or "500ms")
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  
  return parts.join(' ');
}
